import React from 'react';
import { Check, AlertCircle } from 'lucide-react';

const Services = () => {
  const massageServices = [
    { duration: '30 Minutes', description: 'Targeted Muscle Group', price: 'R250' },
    { duration: '45 Minutes', description: 'Up to 2 Muscle Groups', price: 'R350' },
    { duration: '1 Hour', description: 'Up to 3 Muscle Groups', price: 'R450' },
    {
      duration: 'Full Body',
      description: 'Neck, Shoulders, Arms, Back, Glute Release, Legs, Feet (2+ hrs)',
      price: 'R1000',
    },
  ];

  const cuppingServices = [
    { name: 'Dry Cupping', description: 'Up to 10 cups', price: 'R300' },
    { name: 'Wet/Hijama Cupping', description: 'Up to 8 cups', price: 'R350' },
    { name: 'Specific Hijama Sunnah Points', description: 'Up to 8 cups', price: 'R400' },
    { name: 'Each Additional Cup', description: 'Per cup', price: 'R50' },
  ];

  const tapingServices = [
    { name: 'Kinesiology Taping', description: 'Injury site/tape quantity dependent', price: 'R100-R250' },
    { name: 'Rigid Taping', description: 'Injury site/tape quantity dependent', price: 'R100-R250' },
  ];

  const exercisePlanServices = [
    {
      name: 'Exercise Plan',
      description: 'Personalised programme tailored to your goals',
      price: 'From R200',
    },
  ];

  const comboDeal = {
    name: '1 Hour Sports Massage + Sunnah/Hijama Cupping',
    description: 'Up to 8 cups',
    price: 'R750',
  };

  return (
    <section id="services" className="relative py-20 bg-[#F2F6F8] overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-honeycomb opacity-[0.08]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0C3242] mb-4">
            Services & Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional mobile therapy services at competitive rates
          </p>
          <div className="w-20 h-1 brand-rule rounded-full mx-auto mt-6"></div>
        </div>

        {/* Services Grid */}
        <div className="space-y-8">
          {/* Sports Massage */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#0C3242]">Sports Massage / Deep Tissue Massage</h3>
              <img
                src="https://images.unsplash.com/photo-1519823551278-64ac92734fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBtYXNzYWdlfGVufDB8fHx8MTc3MDc0NzMwMHww&ixlib=rb-4.1.0&q=85"
                alt="Sports Massage"
                className="hidden md:block w-16 h-16 rounded-lg object-cover"
              />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {massageServices.map((service, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl p-6 hover:border-[#178E92] hover:shadow-md transition-all duration-200 relative"
                >
                  {service.badge && (
                    <span className="absolute -top-3 right-4 bg-[#136281] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {service.badge}
                    </span>
                  )}
                  <p className="text-lg font-bold text-[#0C3242] mb-2">{service.duration}</p>
                  <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{service.description}</p>
                  <div>
                    {service.special ? (
                      <div>
                        <p className="text-gray-400 line-through text-sm">{service.price}</p>
                        <p className="text-2xl font-bold text-[#178E92]">{service.special}</p>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold text-[#178E92]">{service.price}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cupping */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#0C3242]">Cupping / Hijama</h3>
              <img
                src="https://images.unsplash.com/photo-1621287571777-c725d8e34d39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjdXBwaW5nJTIwdGhlcmFweXxlbnwwfHx8fDE3NzA3NDczMTB8MA&ixlib=rb-4.1.0&q=85"
                alt="Hijama Cupping"
                className="hidden md:block w-16 h-16 rounded-lg object-cover"
              />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {cuppingServices.map((service, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl p-6 hover:border-[#178E92] hover:shadow-md transition-all duration-200"
                >
                  <p className="text-lg font-bold text-[#0C3242] mb-2">{service.name}</p>
                  <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{service.description}</p>
                  <p className="text-2xl font-bold text-[#178E92]">{service.price}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Taping */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-[#0C3242] mb-6">Injury Taping / Strapping</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {tapingServices.map((service, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl p-6 hover:border-[#178E92] hover:shadow-md transition-all duration-200"
                >
                  <p className="text-lg font-bold text-[#0C3242] mb-2">{service.name}</p>
                  <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                  <p className="text-2xl font-bold text-[#178E92]">{service.price}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Exercise Plans */}
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-[#0C3242] mb-6">Exercise Plans</h3>
            <div className="grid gap-4">
              {exercisePlanServices.map((service, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl p-6 hover:border-[#178E92] hover:shadow-md transition-all duration-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div>
                    <p className="text-lg font-bold text-[#0C3242] mb-2">{service.name}</p>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </div>
                  <p className="text-2xl font-bold text-[#178E92] sm:text-right sm:pl-6 whitespace-nowrap">
                    {service.price}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Limited Time Special */}
          <div className="bg-[#E4F0F2] rounded-3xl shadow-xl p-6 md:p-8 flex justify-center border-2 border-[#178E92]">
            <img 
              src="/images/wet-cupping-special.jpg" 
              alt="Limited Wet Cupping Special - Bring a friend and receive R100 off!" 
              className="max-w-full md:max-w-2xl h-auto rounded-2xl shadow-lg hover:scale-[1.02] transition-transform duration-300"
            />
          </div>

          {/* Combo Deal */}
          <div className="bg-gradient-to-r from-[#136281] to-[#178E92] rounded-3xl shadow-xl p-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-6 md:mb-0">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-sm font-semibold mb-3">
                  COMBO DEAL
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold mb-2">{comboDeal.name}</h3>
                <p className="text-white/90">{comboDeal.description}</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-bold mb-2">{comboDeal.price}</p>
                <a
                  href="https://wa.me/27648906906?text=Hi%2C%20I%27d%20like%20to%20book%20the%20Combo%20Deal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-[#136281] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-200"
                >
                  Book Combo
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mt-12 bg-amber-50 border-l-4 border-amber-400 rounded-2xl p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
            <div className="space-y-2">
              <p className="font-semibold text-gray-900">Important Information:</p>
              <ul className="space-y-1 text-gray-700">
                <li className="flex items-start">
                  <Check className="text-amber-600 mr-2 flex-shrink-0 mt-1" size={16} />
                  <span>All prices exclude call-out fees (starting from R100, location dependent)</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-amber-600 mr-2 flex-shrink-0 mt-1" size={16} />
                  <span><strong>Males Only.</strong> Alternative female therapists available upon request.</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-amber-600 mr-2 flex-shrink-0 mt-1" size={16} />
                  <span><strong>Preferred cupping dates are subject to moon sighting.</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
